import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import './index.scss';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {ControlModel} from '../../../../types/control';
import {useSetSelector} from '../../../../hooks/set-selector';
import {NO_OUTPUT_OPTION} from '../ControlEditScreen';
import {useSortBySelector} from '../../../../hooks/sort-by-selector';
import {useHistory} from 'react-router-dom';
import {useLocalization} from "../../../../hooks/localization";
import {useRPC} from '../../../../hooks/rpc';
import {useFilterSelector} from '../../../../hooks/filter-selector';

const keyExtractor = (item: ControlModel) => item.id;

export const ControlListScreen = memo(function ControlListScreen() {
    const {t} = useLocalization();
    const history = useHistory();

    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const controlSet = useSetSelector(state => state.control.controlList)
    const virtualControlList = useFilterSelector(state => state.control.virtualControlList, filter => filter?.parameters?.['is_remote_control'])
    const controlList = useSortBySelector(state => state.control.controlList, control => control.bridge_device_id);

    const remoteControlList: ControlModel[] = useMemo(() => {
        const list = []
        for (const values of virtualControlList) {
            list.push({
                id: values?.id,
                name: values?.parameters?.["remote_control_name"],
                bridge_device_id: values?.bridge_device_id,
                type_id: controlSet[values?.control_id]?.type_id,
            })
        }
        return list
    }, [virtualControlList, controlSet])

    const allControlList = useMemo(() => ([...remoteControlList, ...controlList]), [remoteControlList, controlList])


    const [rpc] = useRPC();
    const [controlCountValue, setControlCountValue] = useState<number>(0);
    const [selectedControl, setSelectedControl] = useState<ControlModel>(null);

    const onCopyClick = useCallback(() => {
        history.push({pathname: '/settings/control/edit', state: {...selectedControl, id: null}});
    }, [history, selectedControl]);

    const buttons = useMemo(() => [
        {
            text: t('copy'),
            onClick: onCopyClick,
            className: 'blue',
            hidden: !(selectedControl),
        },
        {
            text: t('edit'),
            url: `/settings/control/edit/${selectedControl ? selectedControl.id : ''}`,
            className: 'orange',
            hidden: !(selectedControl),
        },
        {
            text: t('create_control'),
            url: '/settings/control/edit',
            className: 'green',
        },
    ], [t, selectedControl, onCopyClick]);

    const displayNameExtractor = useCallback((item: ControlModel, column: ColumnModel) => {
        if (column.key === 'bridge_device_id') {
            return (bridgeDeviceSet[item.bridge_device_id] && bridgeDeviceSet[item.bridge_device_id].name) || '-';
        }

        if (column.key === 'type_id') {
            return controlTypeSet[item.type_id].name;
        }

        if (column.key === 'output_number') {
            let outputString = '';

            if (item.parameters?.output_number != null && item.parameters?.output_number !== NO_OUTPUT_OPTION.value) {
                if (item.parameters?.output_number === 254) {
                    outputString += '0-10V';
                } else {
                    outputString += item.parameters?.output_number;
                }
            }

            // @ts-ignore
            const downOutput = item.parameters?.down_output_number;
            if (downOutput != null) {
                const downOutputString = downOutput === 254 ? '0-10V' : downOutput.toString();
                outputString += outputString === '' ? downOutputString : ' - ' + downOutputString;
            }

            return outputString || '-';
        }

        return item[column.key] || '-';
    }, [bridgeDeviceSet, controlTypeSet]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: `${t('control_name')} (${controlCountValue})`,
            key: 'name',
        },
        {
            length: 2,
            name: t('device'),
            key: 'bridge_device_id',
        },
        {
            length: 3,
            name: t('control_type_name'),
            key: 'type_id',
        },
        {
            length: 1,
            name: t('output'),
            key: 'output_number',
        },
        {
            length: 1,
            name: t('slot'),
            key: 'slot',
        },
    ], [t, controlCountValue]);

    useEffect(() => {
        rpc.getControlCount().then(r => setControlCountValue(r + remoteControlList?.length))
    }, [rpc, remoteControlList])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="camera-list-page" backUrl={'/settings'} searchable={true}>
                <SettingsList
                    columns={columns}
                    items={allControlList}
                    listType='controlList'
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedControl}
                    onItemSelected={setSelectedControl}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
